import React, { useState } from "react";
import {
  Stack,
  Text,
  Button,
  ActionIcon,
  Collapse,
  NumberInput,
  Group,
} from "@mantine/core";
import {
  IconPlus,
  IconTrash,
  IconChevronDown,
  IconChevronUp,
  IconClock,
} from "@tabler/icons-react";
import { SelectedService } from "../../../../../../types/availability";
import AddServiceModal from "./AddServiceModal";
import styles from "./RebookModal.module.css";

interface ConfigureServicesStepProps {
  selectedServices: SelectedService[];
  setSelectedServices: React.Dispatch<React.SetStateAction<SelectedService[]>>;
}

export default function ConfigureServicesStep({
  selectedServices,
  setSelectedServices,
}: ConfigureServicesStepProps) {
  // State for modal
  const [addServiceModalOpened, setAddServiceModalOpened] = useState(false);
  const [expandedServiceId, setExpandedServiceId] = useState<string | null>(
    null,
  );

  // Handle adding a new service from modal
  const handleAddService = (newService: SelectedService) => {
    setSelectedServices((prev) => [...prev, newService]);
  };

  // Handle removing a service
  const handleRemoveService = (serviceId: string) => {
    setSelectedServices((prev) => prev.filter((s) => s.id !== serviceId));
    if (expandedServiceId === serviceId) {
      setExpandedServiceId(null);
    }
  };

  // Handle updating service duration
  const handleUpdateDuration = (
    serviceId: string,
    field: "baseDuration" | "processingTime" | "finishingTime" | "bufferTime",
    value: number,
  ) => {
    setSelectedServices((prev) =>
      prev.map((s) => (s.id === serviceId ? { ...s, [field]: value } : s)),
    );
  };

  // Calculate total duration for a service
  const getTotalDuration = (service: SelectedService) => {
    return (
      service.baseDuration +
      service.processingTime +
      service.finishingTime +
      service.bufferTime +
      service.totalDurationDelta
    );
  };

  // Toggle expanded state
  const toggleExpanded = (serviceId: string) => {
    setExpandedServiceId((prev) => (prev === serviceId ? null : serviceId));
  };

  return (
    <Stack gap="md">
      <Text size="sm" c="dimmed">
        Select services to rebook or add new ones. Adjust durations as needed.
      </Text>

      {/* Selected Services */}
      {selectedServices.length > 0 && (
        <Group justify="space-between">
          <Text fw={600} tt="uppercase" c="dimmed">
            Selected Services ({selectedServices.length})
          </Text>
          {/* Add New Service Button */}
          <Button
            variant="outline"
            color="var(--color-primary)"
            leftSection={<IconPlus size={16} />}
            onClick={() => setAddServiceModalOpened(true)}
          >
            Add Service
          </Button>
        </Group>
      )}
      {selectedServices.map((service) => {
        const isExpanded = expandedServiceId === service.id;
        const totalDuration = getTotalDuration(service);

        return (
          <div key={service.id} className={styles.serviceCard}>
            <div
              className={styles.serviceCardHeader}
              onClick={() => toggleExpanded(service.id)}
            >
              <div className={styles.serviceCardInfo}>
                <div className={styles.serviceCardTitle}>
                  {service.serviceName}
                  {service.isNew && (
                    <span className={styles.newBadge}>New</span>
                  )}
                </div>
                <div className={styles.serviceCardMeta}>
                  {service.staffName} • ${service.price.toFixed(2)} •{" "}
                  <IconClock size={14} /> {totalDuration} min
                </div>
              </div>
              <div className={styles.serviceCardActions}>
                <ActionIcon variant="subtle" color="var(--color-primary)">
                  {isExpanded ? (
                    <IconChevronUp size={20} />
                  ) : (
                    <IconChevronDown size={20} />
                  )}
                </ActionIcon>
                <ActionIcon
                  variant="subtle"
                  color="red"
                  onClick={(e) => {
                    e.stopPropagation();
                    handleRemoveService(service.id);
                  }}
                >
                  <IconTrash size={18} />
                </ActionIcon>
              </div>
            </div>

            <Collapse in={isExpanded}>
              <div className={styles.serviceCardContent}>
                <div className={styles.durationGrid}>
                  <NumberInput
                    label="Base Duration (min)"
                    value={service.baseDuration}
                    onChange={(val) =>
                      handleUpdateDuration(
                        service.id,
                        "baseDuration",
                        Number(val) || 0,
                      )
                    }
                    min={0}
                    step={5}
                  />
                  <NumberInput
                    label="Processing Time (min)"
                    value={service.processingTime}
                    onChange={(val) =>
                      handleUpdateDuration(
                        service.id,
                        "processingTime",
                        Number(val) || 0,
                      )
                    }
                    min={0}
                    step={5}
                  />
                  <NumberInput
                    label="Finishing Time (min)"
                    value={service.finishingTime}
                    onChange={(val) =>
                      handleUpdateDuration(
                        service.id,
                        "finishingTime",
                        Number(val) || 0,
                      )
                    }
                    min={0}
                    step={5}
                  />
                  <NumberInput
                    label="Buffer Time (min)"
                    value={service.bufferTime}
                    onChange={(val) =>
                      handleUpdateDuration(
                        service.id,
                        "bufferTime",
                        Number(val) || 0,
                      )
                    }
                    min={0}
                    step={5}
                  />
                </div>
              </div>
            </Collapse>
          </div>
        );
      })}

      {/* Add Service Modal */}
      <AddServiceModal
        opened={addServiceModalOpened}
        onClose={() => setAddServiceModalOpened(false)}
        onSuccess={handleAddService}
      />
    </Stack>
  );
}
